import { Head, Link, useForm, usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'

import { PlatanusIsotype } from '@/components/icons/PlatanusIsotype'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AppLayout } from '@/layouts/AppLayout'
import { newUserPasswordPath, userSessionPath } from '@/lib/routes'
import type { PageProps } from '@/types/inertia'

function Login() {
  const { props } = usePage<PageProps>()
  const form = useForm({ email: '', password: '' })
  const baseError = (form.errors as Record<string, string>).base || props.errors.base

  return (
    <>
      <Head title="Sign in" />

      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16">
        <div className="mb-12 flex items-center gap-4">
          <PlatanusIsotype className="h-14 w-14" />
          <h1 className="text-4xl font-semibold tracking-tight">Platanus Track</h1>
        </div>

        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ user: data }))
            form.post(userSessionPath())
          }}
        >
          {baseError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {baseError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              autoComplete="email"
              autoFocus
              className="h-11"
              id="email"
              inputMode="email"
              onChange={(event) => form.setData('email', event.target.value)}
              type="email"
              value={form.data.email}
            />
            {form.errors.email && (
              <p className="text-xs text-destructive">{form.errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                href={newUserPasswordPath()}
              >
                Forgot?
              </Link>
            </div>
            <Input
              autoComplete="current-password"
              className="h-11"
              id="password"
              onChange={(event) => form.setData('password', event.target.value)}
              type="password"
              value={form.data.password}
            />
          </div>

          <Button className="h-11 w-full" disabled={form.processing} type="submit">
            {form.processing ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </>
  )
}

export default Object.assign(Login, {
  layout: (page: ReactNode) => <AppLayout>{page}</AppLayout>,
})
